/**
 * Room Gallery (room-gallery.js)
 * Shop by Room tile gallery: style chip prefetch, Load More, and the live
 * product name + price shown under each tile.
 *
 * Prices are read from /products/{handle}.js at view time and are never
 * written into the custom.room_gallery_pool metafield.
 *
 * This file is NOT room-gallery-tracking.js (legacy, never reuse) and it does
 * not modify shop-by-room-tracking.js. The markup contract that file depends
 * on is preserved exactly: article.rg-tile, a.rg-tile__link, and the tile img.
 */
(function () {
  'use strict';

  /* /products/{handle}.js is not edge cached (cf-cache-status: DYNAMIC), so
     every call hits origin. Measured on lights.com: 113 handles at concurrency
     8 returned 22 x HTTP 429; the same set at concurrency 3 returned zero. */
  var CONCURRENCY = 3;
  var ROOT_MARGIN = '400px 0px';
  var MAX_RETRIES = 3;
  var RETRY_BASE_MS = 500;

  var DEBUG = /(?:^|[?&])rg_debug=1(?:&|$)/.test(window.location.search);

  var productCache = {};
  var queue = [];
  var activeRequests = 0;
  var observer = null;
  var observerUnavailable = false;
  var observeCounter = 0;
  var moneyFormatter = null;

  /* Handles in the pool whose product no longer resolves. Exposed for auditing
     the nightly sweep from the console: window.rgMissingHandles */
  var missingHandles = [];
  window.rgMissingHandles = missingHandles;

  /* ---------------------------------------------------------------- money */

  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    if (!moneyFormatter) {
      var currency = 'USD';
      try {
        if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
          currency = window.Shopify.currency.active;
        }
      } catch (e) { /* fall through to USD */ }
      try {
        moneyFormatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency
        });
      } catch (e2) {
        moneyFormatter = { format: function (value) { return '$' + value.toFixed(2); } };
      }
    }
    return moneyFormatter.format(cents / 100);
  }

  /* ------------------------------------------------------- request queue */

  function pump() {
    while (activeRequests < CONCURRENCY && queue.length) {
      var job = queue.shift();
      activeRequests++;
      job().then(releaseSlot, releaseSlot);
    }
  }

  function releaseSlot() {
    activeRequests--;
    pump();
  }

  function enqueue(job) {
    return new Promise(function (resolve) {
      queue.push(function () {
        return job().then(resolve, function () { resolve(null); });
      });
      pump();
    });
  }

  function requestProduct(handle, attempt) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (res) {
      if (res.status === 429 && attempt < MAX_RETRIES) {
        var wait = (RETRY_BASE_MS * Math.pow(2, attempt)) + Math.floor(Math.random() * 250);
        return new Promise(function (resolve) {
          setTimeout(resolve, wait);
        }).then(function () {
          return requestProduct(handle, attempt + 1);
        });
      }
      if (res.status === 404) {
        if (missingHandles.indexOf(handle) === -1) missingHandles.push(handle);
        return null;
      }
      if (!res.ok) return null;
      return res.json();
    }).catch(function () {
      return null;
    });
  }

  function getProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = enqueue(function () {
      return requestProduct(handle, 0);
    });
    return productCache[handle];
  }

  /* ------------------------------------------------------------- helpers */

  function handleFromHref(href) {
    if (!href) return '';
    var match = href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  /* ?rg_debug=1 only. The live catalog has no multi-variant products and no
     on-sale products in any room pool, so these two price shapes cannot be
     seen with real data. This fakes them on every 2nd and 3rd tile so both
     render paths can be checked on the preview theme. Does nothing without
     the query param. */
  function applyDebugShape(product, index) {
    var shape = index % 3;
    if (shape === 0) return product;

    var copy = {};
    for (var key in product) {
      if (Object.prototype.hasOwnProperty.call(product, key)) copy[key] = product[key];
    }
    var base = typeof copy.price_min === 'number' ? copy.price_min : copy.price;

    if (shape === 1) {
      copy.price_varies = true;
      copy.price_max = Math.round(base * 1.5);
      copy.rgDebugShape = 'multi-variant';
    } else {
      copy.compare_at_price_max = Math.round(base * 1.4);
      copy.compare_at_price_min = copy.compare_at_price_max;
      copy.compare_at_price_varies = false;
      copy.rgDebugShape = 'on-sale';
    }
    return copy;
  }

  function updateLabel(tile, product, priceText) {
    var link = tile.querySelector('a.rg-tile__link');
    if (!link) return;

    var base = link.getAttribute('data-rg-label');
    if (base === null) {
      base = link.getAttribute('aria-label') || '';
      link.setAttribute('data-rg-label', base);
    }

    var parts = [];
    if (base) parts.push(base);
    if (product.title && base.indexOf(product.title) === -1) parts.push(product.title);
    if (priceText) parts.push(priceText);

    link.setAttribute('aria-label', parts.join(', '));
  }

  /* ----------------------------------------------------------- rendering */

  function renderTile(tile, product) {
    tile.classList.remove('rg-tile--pending');

    if (!product) {
      tile.classList.add('rg-tile--unresolved');
      return;
    }

    var nameEl = tile.querySelector('[data-rg-name]');
    var priceEl = tile.querySelector('[data-rg-price]');

    if (nameEl) nameEl.textContent = product.title || '';

    var priceMin = typeof product.price_min === 'number' ? product.price_min : product.price;
    var compareAt = product.compare_at_price_max || 0;
    var onSale = compareAt > priceMin;
    var priceText = product.price_varies
      ? 'From ' + formatMoney(priceMin)
      : formatMoney(priceMin);

    if (priceEl) {
      priceEl.textContent = '';
      var current = document.createElement('span');
      current.className = 'rg-tile__price-current';
      if (onSale) current.className += ' rg-tile__price-current--sale';
      current.textContent = priceText;
      priceEl.appendChild(current);

      if (onSale) {
        var was = document.createElement('s');
        was.className = 'rg-tile__price-was';
        was.textContent = formatMoney(compareAt);
        priceEl.appendChild(document.createTextNode(' '));
        priceEl.appendChild(was);
      }
    }

    if (DEBUG && product.rgDebugShape) tile.setAttribute('data-rg-debug-shape', product.rgDebugShape);

    updateLabel(tile, product, priceText);
  }

  function hydrate(tile) {
    if (tile.getAttribute('data-rg-state')) return;
    tile.setAttribute('data-rg-state', 'loading');

    var link = tile.querySelector('a.rg-tile__link');
    var handle = link ? handleFromHref(link.getAttribute('href')) : '';

    if (!handle) {
      tile.setAttribute('data-rg-state', 'skipped');
      tile.classList.remove('rg-tile--pending');
      return;
    }

    var index = parseInt(tile.getAttribute('data-rg-index'), 10) || 0;

    getProduct(handle).then(function (product) {
      tile.setAttribute('data-rg-state', product ? 'ready' : 'missing');
      renderTile(tile, (DEBUG && product) ? applyDebugShape(product, index) : product);
    });
  }

  function observeTiles(tiles) {
    if (!tiles || !tiles.length) return;

    if (!observer && !observerUnavailable) {
      if (!('IntersectionObserver' in window) || typeof window.fetch !== 'function') {
        observerUnavailable = true;
      } else {
        observer = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            observer.unobserve(entries[i].target);
            hydrate(entries[i].target);
          }
        }, { rootMargin: ROOT_MARGIN, threshold: 0.01 });
      }
    }

    for (var j = 0; j < tiles.length; j++) {
      var tile = tiles[j];
      if (tile.getAttribute('data-rg-observed')) continue;
      tile.setAttribute('data-rg-observed', '1');
      tile.setAttribute('data-rg-index', String(observeCounter++));

      if (observerUnavailable) {
        /* No IntersectionObserver or no fetch: leave the reserved space blank
           rather than firing a flood of requests. */
        tile.classList.remove('rg-tile--pending');
        continue;
      }
      observer.observe(tile);
    }
  }

  /* ------------------------------------------------------ chip prefetch */

  function initChipPrefetch(gallery) {
    var chips = gallery.querySelectorAll('.rg-chip[href]');
    var prefetched = {};
    Array.prototype.forEach.call(chips, function (chip) {
      chip.addEventListener('mouseenter', function () {
        var href = chip.getAttribute('href');
        if (prefetched[href]) return;
        prefetched[href] = true;
        var link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = href;
        document.head.appendChild(link);
      }, { passive: true });
    });
  }

  /* -------------------------------------------------------- load more */

  function initLoadMore(gallery) {
    var btn = document.querySelector('.rg-loadmore');
    if (!btn) return;
    var tmpl = document.getElementById('rg-more-tiles');
    if (!tmpl) return;
    var firstTile = gallery.querySelector('.rg-tile');
    if (!firstTile) return;

    var grid = firstTile.parentNode;
    var batch = parseInt(btn.getAttribute('data-batch'), 10) || 60;

    btn.addEventListener('click', function () {
      var frag = tmpl.content;
      var added = [];

      for (var i = 0; i < batch; i++) {
        var next = frag.firstElementChild;
        if (!next) break;
        grid.appendChild(next);
        added.push(next);
      }

      var remaining = frag.querySelectorAll('.rg-tile').length;
      if (remaining === 0) btn.classList.add('rg-loadmore--hidden');

      /* Only the tiles just revealed are observed; already-loaded tiles keep
         their data and are never refetched. */
      observeTiles(added);
    });
  }

  /* --------------------------------------------------------------- init */

  function init() {
    var gallery = document.querySelector('[data-rg-root]');
    if (!gallery) return;

    initChipPrefetch(gallery);
    initLoadMore(gallery);
    observeTiles(gallery.querySelectorAll('.rg-tile'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
